import { useCallback, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ArrowRight, CheckCircle2, TrendingUp, Zap } from 'lucide-react'

import { useWcRecommendations } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type { WcRecommendation } from './types'

type WcStatus = 'open' | 'actioned'

function normalizeStatus(status: string): WcStatus {
  return status === 'actioned' ? 'actioned' : 'open'
}

function parsePctString(raw: string): number | null {
  const trimmed = String(raw).trim()
  if (!trimmed) return null
  const match = trimmed.match(/^([\d.+-]+)\s*%?$/)
  if (!match) return null
  const numeric = Number(match[1])
  return Number.isFinite(numeric) ? numeric : null
}

const TYPE_BUCKETS: Record<string, { label: string; color: string }> = {
  accelerate_ar: { label: 'AR Acceleration', color: '#10b981' },
  ar_acceleration: { label: 'AR Acceleration', color: '#10b981' },
  'ar acceleration': { label: 'AR Acceleration', color: '#10b981' },
  stretch_ap: { label: 'AP Extension', color: '#3b82f6' },
  ap_extension: { label: 'AP Extension', color: '#3b82f6' },
  'ap extension': { label: 'AP Extension', color: '#3b82f6' },
  optimize_inventory: { label: 'Inventory', color: '#8b5cf6' },
  inventory: { label: 'Inventory', color: '#8b5cf6' },
}

function getTypeStyle(raw: string) {
  const key = raw.trim().toLowerCase()
  return TYPE_BUCKETS[key] ?? { label: raw.trim() || 'Other', color: colors.text.muted }
}

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

export default function WcRecommendationsPage() {
  const wcQuery = useWcRecommendations()
  const { data: recommendations = [], isLoading, isError, error, refetch } = wcQuery

  const sorted = useMemo(
    (): WcRecommendation[] =>
      [...recommendations].sort((a, b) => a.priority_rank - b.priority_rank),
    [recommendations],
  )

  const [statuses, setStatuses] = useState<Record<string, WcStatus>>({})

  const effectiveStatus = useCallback(
    (recommendation: WcRecommendation) =>
      statuses[recommendation.recommendation_id] ??
      normalizeStatus(recommendation.status),
    [statuses],
  )

  const toggleStatus = (id: string) => {
    setStatuses(previous => {
      const recommendation = recommendations.find(item => item.recommendation_id === id)
      const baseline = recommendation ? normalizeStatus(recommendation.status) : 'open'
      const current = previous[id] ?? baseline
      return { ...previous, [id]: current === 'actioned' ? 'open' : 'actioned' }
    })
  }

  const totalCashImpact = useMemo(
    () => recommendations.reduce((sum, recommendation) => sum + recommendation.cash_impact, 0),
    [recommendations],
  )

  const openCount = useMemo(
    () =>
      recommendations.filter(
        recommendation => effectiveStatus(recommendation) === 'open',
      ).length,
    [recommendations, effectiveStatus],
  )

  const actionedCount = useMemo(
    () =>
      recommendations.filter(
        recommendation => effectiveStatus(recommendation) === 'actioned',
      ).length,
    [recommendations, effectiveStatus],
  )

  const impactByType = useMemo(() => {
    const groups: Record<string, { label: string; value: number; color: string }> = {}
    for (const recommendation of recommendations) {
      const { label, color } = getTypeStyle(recommendation.type)
      if (!groups[label]) groups[label] = { label, value: 0, color }
      groups[label].value += recommendation.cash_impact
    }
    return Object.values(groups)
  }, [recommendations])

  if (isLoading) {
    return <CashflowV2Loading label="Loading working capital recommendations…" />
  }

  if (isError) {
    if (looksUnconfigured(error)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load working capital recommendations"
        description={error instanceof Error ? error.message : undefined}
        onRetry={() => refetch()}
      />
    )
  }

  const chartHeight = Math.max(200, impactByType.length * 44)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <TrendingUp size={24} className="text-emerald-500 shrink-0" />
          Working Capital Recommendations
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          AI-generated recommendations to optimize cash conversion cycle timing.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label="Total Cash Impact"
          value={fmt.compact(totalCashImpact)}
          color={colors.status.green}
          subtext="Across all recommendations"
        />
        <KpiCard
          label="Open Items"
          value={String(openCount)}
          color={colors.status.yellow}
          subtext="Awaiting action"
        />
        <KpiCard
          label="Actioned Items"
          value={String(actionedCount)}
          color={colors.status.green}
          subtext="Marked actioned in this view"
        />
      </div>

      <div className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5">
        <h3 className="text-gray-900 dark:text-gray-100 font-semibold text-sm mb-4">
          Cash Impact by Type
        </h3>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart layout="vertical" data={impactByType} margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.chart.gridLine}
              horizontal={false}
            />
            <XAxis
              type="number"
              tickFormatter={(value: number) => fmt.compact(value)}
              tick={{ fill: colors.text.muted, fontSize: 11 }}
              axisLine={{ stroke: colors.chart.gridLine }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fill: colors.text.secondary, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={140}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.bg.surface,
                border: `1px solid ${colors.chart.gridLine}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: colors.text.primary }}
              formatter={(value: unknown) => [fmt.currency(value as number), 'Cash Impact']}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
              {impactByType.map((entry, index) => (
                <Cell key={index} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-4">
        <h3 className="text-gray-900 dark:text-gray-100 font-semibold text-sm">
          All Recommendations ({sorted.length})
        </h3>

        {sorted.map(recommendation => {
          const typeCfg = getTypeStyle(recommendation.type)
          const isActioned = effectiveStatus(recommendation) === 'actioned'
          const cocNum = parsePctString(recommendation.cost_of_capital_comparison)
          const cocDisplay =
            cocNum != null
              ? fmt.pct(cocNum)
              : recommendation.cost_of_capital_comparison || '—'

          return (
            <div
              key={recommendation.recommendation_id}
              className="rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-dark-border p-5 space-y-3"
              style={{ borderLeftWidth: 3, borderLeftColor: typeCfg.color }}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold shrink-0"
                    style={{
                      backgroundColor: `${colors.accent.teal}25`,
                      color: colors.accent.teal,
                      border: `1px solid ${colors.accent.teal}40`,
                    }}
                  >
                    #{recommendation.priority_rank}
                  </span>

                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shrink-0"
                    style={{
                      backgroundColor: `${typeCfg.color}20`,
                      color: typeCfg.color,
                      border: `1px solid ${typeCfg.color}40`,
                    }}
                  >
                    {typeCfg.label}
                  </span>

                  <span className="text-gray-900 dark:text-gray-100 font-medium text-sm truncate">
                    {recommendation.counterparty}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 text-xs font-mono shrink-0">
                    {recommendation.target_week}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => toggleStatus(recommendation.recommendation_id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors shrink-0"
                  style={{
                    backgroundColor: isActioned
                      ? `${colors.status.green}20`
                      : `${colors.status.yellow}20`,
                    color: isActioned ? colors.status.green : colors.status.yellow,
                    border: `1px solid ${
                      isActioned
                        ? `${colors.status.green}40`
                        : `${colors.status.yellow}40`
                    }`,
                  }}
                >
                  {isActioned ? <CheckCircle2 size={12} /> : <Zap size={12} />}
                  {isActioned ? 'Actioned' : 'Open'}
                </button>
              </div>

              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {recommendation.rationale}
              </p>

              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 dark:text-gray-500 text-xs">Timing:</span>
                  <span className="font-mono text-gray-600 dark:text-gray-300 text-sm">
                    {recommendation.current_timing_days}d
                  </span>
                  <ArrowRight size={14} className="text-teal-500 shrink-0" />
                  <span className="font-mono text-teal-600 text-sm font-semibold">
                    {recommendation.recommended_timing_days}d
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-gray-400 dark:text-gray-500 text-xs">Cash Impact:</span>
                  <span className="font-mono text-emerald-600 text-base font-bold">
                    {fmt.compact(recommendation.cash_impact)}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-400 dark:text-gray-500 text-xs">IRR:</span>
                  <span
                    className="font-mono text-sm font-medium"
                    style={{
                      color:
                        cocNum != null && recommendation.annualized_irr > cocNum
                          ? colors.status.green
                          : cocNum != null
                            ? colors.status.red
                            : colors.text.primary,
                    }}
                  >
                    {fmt.pct(recommendation.annualized_irr)}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 text-xs">vs CoC</span>
                  <span className="font-mono text-gray-600 dark:text-gray-300 text-sm">
                    {cocDisplay}
                  </span>
                </div>
              </div>

              <div className="inline-flex items-center gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-700">
                Eliza AI
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
